// 这个保留，后期可能需要单独添加地区的选择器
// 如果是这样就将RegionAndLanguageSelect组件中的region组件抽离出来

import React, { useEffect, useMemo, useState } from 'react';
import type { RegionType } from '../../types';
import { commonBackInfo, compareArraysEqual } from '../../utils';
import {
  accountRegionTitleLocal,
  popularList,
  regionRadioOptions,
  regionTitleLocal,
} from '../../constants/regionAndLanguage';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { getCountryFlag } from '@/pages/home/_config/countries';
import useDefaultTwoLevelFilter from '../../hooks/useDefaultTwoLevelFilter';
import { useIntl } from 'umi';
import { addFilterBuriedPoint } from '../../utils/point';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import { Radio } from 'tea-component';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { hasLogicGame } from '../../constants/game';
import useLanguage from '@op/hooks/useLanguage';

// ============ Region ContentHeader 组件 ============
interface RegionContentHeaderProps {
  logic: 'and' | 'or';
  setLogic: (value: 'and' | 'or') => void;
  showLogicOptions?: boolean;
}

function RegionContentHeader({
  logic,
  setLogic,
  showLogicOptions,
}: RegionContentHeaderProps) {
  const { formatMessage } = useIntl();

  if (!showLogicOptions) {
    return null;
  }

  return (
    <div className="dbo-region-logic">
      <Radio.Group
        value={logic}
        className="dbo-region-radio-box"
        onChange={(value: string) => setLogic(value as 'and')}
      >
        <BubbleCommon
          tipsMap={{
            en: `The selection buttons here determine the combined use logic of the "Region" and "Language" filters: if "AND" is selected, both the language and region conditions must be met; if "OR" is selected, only one of the language or region conditions needs to be met.`,
            zh: `此处的或/且按钮决定"区域"与"语种"筛选器的联合使用逻辑：如果选择“或”，则只需满足语种或者区域任一个条件；如果选择“且”，则表示需要同时满足语种跟区域的条件。`,
          }}
        >
          <Radio name="and">{formatMessage({ id: 'and' })}</Radio>
        </BubbleCommon>
        <Radio name="or">{formatMessage({ id: 'or' })}</Radio>
      </Radio.Group>

      <div></div>
    </div>
  );
}

const regionGlobalTipsMap: {
  common: string;
  influencer: string;
  urlCommon: undefined;
} = {
  common:
    'Global refers to the reviews from game store and social media, which cannot be distinguished from regions/countries.',
  influencer: 'Global data includes Influencer with no country/region defined.',
  urlCommon: undefined,
};

const regionGlobalWoChinaTipsMap: {
  common: string;
  influencer: string;
  urlCommon: undefined;
} = {
  common:
    'Global except China mainland refers to the reviews from game store and social media, that are collected from all regions/countries except China mainland, which cannot be distinguished from regions/countries.',
  influencer:
    'Global except China mainland refers to the reviews from game store and social media, that are collected from all regions/countries except China mainland, which cannot be distinguished from regions/countries.',
  urlCommon: undefined,
};

export default function RegionSelect({
  changeOnCloseWindow,
  onChange,
  defaultValue,
  filterValue,
  defaultKey,
  lang,
  filterOptions,
  oriDefaultFilter,
  isAccount = false,
  regionGolobalTipsKey = 'common',
  regionglobalWoChinaKey = 'common',
  showTips = true,
  needExcludeRegion = false,
  gameInfo,
}: RegionType) {
  const showLogicOptions = hasLogicGame(gameInfo);
  const [regionOptions, setRegionOPtions] = useState<Array<any>>([]);
  const [logic, setLogic] = useState<'and' | 'or'>(
    showLogicOptions ? defaultValue?.logic : 'and',
  );
  // const regionOptions = useMemo(() => {}, [filterOptions]);
  const { formatMessage } = useLanguage();
  useEffect(() => {
    let region: Array<any> = [];
    if (filterOptions) {
      const localFilterOptions =
        filterOptions.region_country || filterOptions || [];

      const list =
        localFilterOptions?.map?.(
          ({ region_key, country, region_name_cn, region_name_en }: any) => ({
            value: region_key?.toLocaleLowerCase?.(),
            text: region_name_en,
            textCN: region_name_cn,
            children: (country || [])?.map?.(
              ({
                country_name_abbre,
                country_name_cn,
                country_name_en,
              }: any) => ({
                value: country_name_abbre?.toLocaleLowerCase?.(),
                text: country_name_en,
                textCN: country_name_cn,
                icon: getCountryFlag(country_name_abbre),
              }),
            ),
          }),
        ) || [];

      list?.unshift({
        value: 'unidentified',
        textCN: '未区分国家/地区',
        text: 'Unidentified',
        children: [
          {
            value: 'Unidentified',
            textCN: '未区分国家/地区',
            text: 'Unidentified',
          },
        ],
      });
      region = list;
    }
    setRegionOPtions(region);
  }, [filterOptions]);
  const {
    setSelectValue,
    selectValue,
    onChange: onRegionChange,
    diffClassName,
    isFirst,
  } = useDefaultTwoLevelFilter({
    originalOptions: regionOptions,
    defaultValue,
    filterValue,
    oriDefaultFilter,
    dealwithDefaultValue: (value: any) => {
      let { region, region_type } = value || {
        region: [],
        region_type: 'market',
      };
      let marketsValue: 'global' | 'global_wo_china' = 'global';
      region = region?.filter((item: string) => item !== 'global');
      const globalWoChinaValue = regionOptions
        .map((item) => item.value)
        .filter((item) => item !== 'chn');
      if (compareArraysEqual(globalWoChinaValue, region)) {
        marketsValue = 'global_wo_china';
        region = [];
      }

      return { value: region || [], type: region_type, marketsValue };
    },
  });

  // 监听 filterValue 变化更新 monitorRule
  // 利用 useDefaultTwoLevelFilter 已处理首次初始化，只需监听后续变化
  useEffect(() => {
    if (!isFirst.current && showLogicOptions) {
      setLogic(filterValue.logic || 'and');
    }
  }, [filterValue]);

  const regionChange = (tags: any) => {
    setSelectValue(tags);
  };

  const { buttonElementMemo, reginTitleConfig } = useMemo(() => {
    const language = isAccount ? accountRegionTitleLocal : regionTitleLocal;
    const key = lang === 'zh-CN' ? 'cn' : 'en';
    const { value, isGLOBAL_WO_CHINA } = onRegionChange(selectValue);

    // 处理一下”未区分国家的评论”选项
    const index = value.indexOf('Unidentified');
    if (index > -1) value.splice(index, 1, 'global');

    let region: any = value.length;
    if (value.length === 0) {
      region = 'Global';
    } else if (isGLOBAL_WO_CHINA) {
      region = 'Global except China mainland';
    }

    return {
      buttonElementMemo: (
        <div>
          <span className="select-button-text">
            {language[key].button_region}
            {showLogicOptions ? `(${formatMessage({ id: logic })})` : ''}
          </span>
          {typeof region === 'number' ? region : formatMessage({ id: region })}
        </div>
      ),
      reginTitleConfig: language,
    };
  }, [selectValue, formatMessage, logic]);

  function getCommonBackInfo() {
    const { value, type, isGLOBAL_WO_CHINA } = onRegionChange(selectValue);

    // 处理一下”未区分国家的评论”选项
    const index = value.indexOf('Unidentified');
    if (index > -1) value.splice(index, 1, 'global');

    let region: any = value.length;
    if (value.length === 0) {
      region = 'Global';
    } else if (isGLOBAL_WO_CHINA) {
      region = 'Global except China mainland';
    }

    return commonBackInfo(
      {
        region: value,
        region_type: type,
        logic: showLogicOptions ? logic : 'and',
        ...(needExcludeRegion ? { wo_china: isGLOBAL_WO_CHINA } : {}),
      },
      { region, logic },
    );
  }

  useEffect(() => {
    onChange?.('region', getCommonBackInfo());
  }, [selectValue, formatMessage, logic]);

  useEffect(() => {
    const data = getCommonBackInfo();
    changeOnCloseWindow?.(data.value);
  }, [selectValue]);

  const diffClassNameMemo = useMemo(() => {
    if (diffClassName) return diffClassName;

    if (logic === 'or') return 'dbo-diff-filter';

    return '';
  }, [diffClassName, logic]);

  return (
    <div className={`region-select-wrapper ${diffClassNameMemo}`}>
      <NewRegionSelect
        popularList={popularList}
        locale={lang}
        language={reginTitleConfig}
        radioOptions={regionRadioOptions(regionGolobalTipsKey)}
        value={selectValue}
        options={regionOptions}
        button={buttonElementMemo}
        header={
          <RegionContentHeader
            logic={logic}
            setLogic={setLogic}
            showLogicOptions={showLogicOptions}
          />
        }
        radioOptionsTips={{
          global: formatMessage({
            id: regionGlobalTipsMap[regionGolobalTipsKey],
          }),
          global_wo_china: formatMessage({
            id: regionGlobalWoChinaTipsMap[regionglobalWoChinaKey],
          }),
        }}
        closeOnScroll={true}
        onCloseReginSelect={(result: any) => {
          regionChange(result);

          try {
            const { value: selectValue, type } = onRegionChange(result);
            // 处理一下”未区分国家的评论”选项
            const index = selectValue.indexOf('Unidentified');
            if (index > -1) selectValue.splice(index, 1, 'global');

            addFilterBuriedPoint(FilterButtonPoint.region, {
              region: selectValue,
              region_type: type,
            });
          } catch (error) {}
        }}
      />
      {showTips && (
        <p className="dbo-disabled-tips">
          {formatMessage({
            id: 'We recommend language filter. Region filter only fits some official accounts and creators.',
          })}
        </p>
      )}
    </div>
  );
}
